import JsonView from "@uiw/react-json-view";
import { githubLightTheme } from "@uiw/react-json-view/githubLight";
import { useEffect, useMemo, useRef, useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import CustomRadioButton from "../../customRadioButton/customRadioButton";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";

interface ValidateEvModalProps {
  onClose: () => void;
  id: string;
  activeTabIndex: number;
}

const ValidateEvModal = (props: ValidateEvModalProps) => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const org_id = getOrgIdForFetchExternalWorklist();

  const [screenshotsUrl, setScreenshotsUrl] = useState<{
    accumulator: string;
    benefits: string;
  }>({
    accumulator: "",
    benefits: "",
  });
  const [screenshotsRef, setScreenshotsRef] = useState<{
    accumulator: string;
    benefits: string;
  }>({
    accumulator: "",
    benefits: "",
  });
  const [selectedOption, setSelectedOption] = useState<string>("raw_response");
  const { medicalPaCoveragerawResponse, loading } = useSelector(
    (state: RootState) => state.medicalPaCoveragerawResponse,
  );

  const isEventTriggerRef = useRef(false);

  const [isLoading, setIsLoading] = useState({
    accumulator: true,
    benefits: true,
  });

  const getCoverageType = (activeTabIndex: number): string => {
    switch (activeTabIndex) {
      case 0:
        return "primary";
      case 1:
        return "secondary";
      case 2:
        return "tertiary";
      default:
        return "primary";
    }
  };

  const checkIfScreenshotsExist = useMemo(() => {
    return screenshotsRef.accumulator !== "" || screenshotsRef.benefits !== "";
  }, [screenshotsRef]);

  const filteredData = useMemo(() => {
    const coverageType = getCoverageType(props.activeTabIndex);

    if (Array.isArray(medicalPaCoveragerawResponse)) {
      const directMatch = medicalPaCoveragerawResponse.find(
        (item) => item.id === coverageType,
      );

      if (directMatch) {
        return {
          [`${coverageType}_coverage`]: directMatch,
        };
      }
    }
    return {
      message: `Could not find ${coverageType} coverage data in the response.`,
    };
  }, [medicalPaCoveragerawResponse, props.activeTabIndex, loading]);

  useEffect(() => {
    if (medicalPaCoveragerawResponse && !loading) {
      const coverageType = getCoverageType(props.activeTabIndex);
      const requiredCoverageData = medicalPaCoveragerawResponse.find(
        (item) => item.id === coverageType,
      );

      setIsLoading({ accumulator: true, benefits: true });

      const acccumulatorScreenshotRef =
        requiredCoverageData?.raw_response?.accumulator_screenshot ?? "";
      const benefits_screenshot_ref =
        requiredCoverageData?.raw_response?.benefits_screenshot ?? "";

      setScreenshotsRef({
        accumulator: acccumulatorScreenshotRef,
        benefits: benefits_screenshot_ref,
      });

      !isEventTriggerRef.current &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id,
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: org_id ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name: EventsName.MODAL_SHOWN,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            modal_name: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
            insurance_type: getCoverageType(props.activeTabIndex),
            screen_type: selectedOption,
            payer_name: payerName,
          },
        });

      !isEventTriggerRef.current &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id,
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: org_id ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name:
            EventsName.VALIDATE_EV_OPTION_SELECTED + " (Raw Response)",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            modal_type: ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
            view_type: getCoverageType(props.activeTabIndex),
            screen_type: "raw_response",
            payer_name: payerName,
          },
        });

      isEventTriggerRef.current = true;
    }
  }, [medicalPaCoveragerawResponse, props.activeTabIndex, loading]);

  useEffect(() => {
    if (screenshotsRef.accumulator) {
      getFileDownloadUrl(screenshotsRef.accumulator).then((url) => {
        setScreenshotsUrl((prev) => ({ ...prev, accumulator: url }));
      });
    }
  }, [screenshotsRef.accumulator]);

  useEffect(() => {
    if (screenshotsRef.benefits) {
      getFileDownloadUrl(screenshotsRef.benefits).then((url) => {
        setScreenshotsUrl((prev) => ({ ...prev, benefits: url }));
      });
    }
  }, [screenshotsRef.benefits]);

  const handleLoad = (type: "accumulator" | "benefits") => {
    setIsLoading((prev) => ({ ...prev, [type]: false }));
  };

  const handleError = (type: "accumulator" | "benefits") => {
    setIsLoading((prev) => ({ ...prev, [type]: false }));
  };

  return (
    <Modal
      dialogId="validate-ev-modal"
      title={`Validate EV Data`}
      saveButtonText="Save"
      cancelText="Cancel"
      onSave={() => {}}
      heightPercentage={70}
      onClose={() => {
        props.onClose();
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id,
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: org_id ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name: EventsName.CLOSE_BUTTON_CLICKED,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            patient_name:
              singleMedicalPaOrder?.demographics?.first_name +
              " " +
              singleMedicalPaOrder?.demographics?.last_name,
            source: "validate_ev_box",
            payer_name: payerName,
          },
        });
      }}
      hideFooter={true}
    >
      <div className="cmm-input--viewer flex h-full flex-col gap-2 rounded-md">
        <div className="cmm-input--viewer options flex items-center gap-4">
          {!loading && (
            <>
              <div className="cmm-input--viewer all-options flex items-center gap-2">
                <CustomRadioButton
                  label=" View Raw Response"
                  value="raw_response"
                  onChange={() => {
                    setSelectedOption("raw_response");

                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id,
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: org_id ?? "",
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      event_name:
                        EventsName.VALIDATE_EV_OPTION_SELECTED +
                        " (Raw Response)",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                        modal_type:
                          ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
                        view_type: getCoverageType(props.activeTabIndex),
                        screen_type: "raw_response",
                        payer_name: payerName,
                      },
                    });
                  }}
                  checked={selectedOption === "raw_response"}
                  name={"options"}
                  className="mb-0"
                />
                <CustomRadioButton
                  label=" View Screenshots"
                  value="screenshots"
                  onChange={() => {
                    setSelectedOption("screenshots");
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id,
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: org_id ?? "",
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      event_name:
                        EventsName.VALIDATE_EV_OPTION_SELECTED +
                        " (Screenshots)",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                        modal_type:
                          ModalNamesForBigQuery.VALIDATE_EV_BV_DATA_MODAL,
                        view_type: getCoverageType(props.activeTabIndex),
                        screen_type: "screenshots",
                        payer_name: payerName,
                      },
                    });
                  }}
                  checked={selectedOption === "screenshots"}
                  name={"options"}
                  className="mb-0"
                />
              </div>
            </>
          )}
        </div>
        <div className="cmm-input--viewer mt-4 h-full rounded-md">
          {selectedOption === "raw_response" &&
            (loading ? (
              <LoaderMessage message="Loading data..." />
            ) : (
              <JsonView
                style={githubLightTheme}
                value={filteredData || { message: "Loading data..." }}
                displayDataTypes={false}
              />
            ))}
          {selectedOption === "screenshots" && (
            <>
              {checkIfScreenshotsExist ? (
                <div className="cmm-input--viewer flex flex-col gap-4">
                  {screenshotsUrl.accumulator !== "" && (
                    <>
                      {isLoading.accumulator && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
                        </div>
                      )}
                      <Zoom>
                        <img
                          src={screenshotsUrl.accumulator ?? ""}
                          alt="Accumulator Page"
                          onLoad={() => handleLoad("accumulator")}
                          onError={() => handleError("accumulator")}
                          className={isLoading.accumulator ? "hidden" : "block"}
                        />
                      </Zoom>
                    </>
                  )}
                  {screenshotsUrl.benefits !== "" && (
                    <>
                      {isLoading.benefits && (
                        <>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
                          </div>
                        </>
                      )}
                      <Zoom>
                        <img
                          src={screenshotsUrl.benefits ?? ""}
                          alt="Benefits Page"
                          onLoad={() => handleLoad("benefits")}
                          onError={() => handleError("benefits")}
                          className={isLoading.benefits ? "hidden" : "block"}
                        />
                      </Zoom>
                    </>
                  )}
                </div>
              ) : (
                <div className="cmm-input--viewer flex h-full flex-col gap-4">
                  <div className="cmm-input--viewer mt-8 flex h-full items-start justify-center text-sm text-primaryGray-5">
                    No screenshots found for this coverage type.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ValidateEvModal;
